import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { ConfirmacionGeneral, TONOS_DE_CONFIRMACION } from '../../../nucleo/componentes/ConfirmacionGeneral.jsx';
import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { CintaDeVersionNueva } from '../../../nucleo/pwa/CintaDeVersionNueva.jsx';
import {
  consultarReconciliacionDeDiferido, descontarRespaldo, ESTADOS_DE_RECONCILIACION,
} from '../api/vencimientosApi.js';
import { importe as formatearImporte } from '../formato.js';

/**
 * LO QUE DECLARASTE Y TUS PLANES (RF-259, RF-263; prototipo v42
 * `-reconciliacion`).
 *
 * LA CONDICIÓN EXPRESA DE Deisy Paez, ASENTADA LITERAL EN EL MAPA DEL
 * INCREMENTO: esta pantalla **REPORTA cualquier diferencia entre lo declarado y
 * lo real; NO la ajusta en silencio**. Abrirla no cambia ninguna cifra: la
 * consulta es de solo lectura y no deja un solo asiento en la bitácora.
 *
 * LA ÚNICA EXCEPCIÓN ES EL BOTÓN, y lo es porque no es silencioso: lo dispara
 * ella, UNA TARJETA POR DISPARO (RN-727a), tras una confirmación que le enseña la
 * cifra de ANTES y la de DESPUÉS, el cambio queda en el historial de la tarjeta y
 * el botón DESAPARECE en cuanto la tarjeta queda en orden —así no hay segundo
 * disparo que reste de más (RN-727e)—.
 *
 * TODOS LOS TEXTOS LOS ARMA EL SERVIDOR (RN-733, RNF-130): la etiqueta, el
 * título y el cuerpo de cada resultado son LITERALES NORMATIVOS y esta pantalla
 * los pinta tal cual, sin variarlos ni componerlos. Tampoco deduce el resultado:
 * lo recibe en `claveEstado`.
 *
 * LA TARJETA DE LA OTRA PERSONA SE VE COMPLETA Y NO SE OPERA (RN-735, D-424):
 * todos ven todo, y lo único que cambia es que no se le ofrece el botón. La
 * defensa real está en el servidor, no aquí.
 */
export default function ReconciliacionDeDiferido() {
  const hoy = obtenerFechaDeNegocioHoy();

  const [reporte, setReporte] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [porConfirmar, setPorConfirmar] = useState(null);
  const [enCurso, setEnCurso] = useState(false);
  const [acuse, setAcuse] = useState(null);
  const [rechazo, setRechazo] = useState(null);

  const consultar = useCallback(async () => {
    setCargando(true);
    try {
      setError(null);
      setReporte(await consultarReconciliacionDeDiferido({ fechaLocal: hoy }));
    } catch (fallo) {
      setError(fallo);
    } finally {
      setCargando(false);
    }
  }, [hoy]);

  useEffect(() => { consultar(); }, [consultar]);

  /**
   * EL DISPARO. Viajan los DOS valores observados —los que ella acaba de ver—:
   * si alguno se movió entre tanto, el servidor no escribe nada y aquí se vuelve
   * a consultar con las cifras de ahora (RN-727d).
   */
  async function confirmarDescuento() {
    const tarjeta = porConfirmar;
    if (!tarjeta) return;

    setEnCurso(true);
    setRechazo(null);
    try {
      const renglon = await descontarRespaldo({
        idCuenta: tarjeta.idCuenta,
        importeDiferidoDeclaradoObservado: tarjeta.importeDiferidoDeclarado,
        importeRespaldoSinDescontarObservado: tarjeta.importeRespaldoSinDescontar,
        fechaLocal: hoy,
      });

      setPorConfirmar(null);
      setAcuse({ tarjeta, renglon });
      await consultar();
    } catch (fallo) {
      // NO SE ESCRIBIÓ NADA. El mensaje es el que redactó el servidor —el del
      // dato movido o el de la cifra que ya no cabe en la deuda (RN-727d,
      // RN-734)— y se vuelve a consultar el reporte con las cifras de ahora.
      setPorConfirmar(null);
      setRechazo(fallo);
      await consultar();
    } finally {
      setEnCurso(false);
    }
  }

  return (
    <Shell titulo="Lo que declaraste y tus planes" volverA="/cuentas/vencimientos">
      <CintaDeVersionNueva />

      <div className="aviso i" role="status" data-prueba="rec-intro">
        <b className="tit">Qué compara esta pantalla</b>
        Tus compras a meses viven en dos partes que se suman: lo que declaraste a mano y que todavía no
        tiene su compra registrada, y lo que respaldan los planes que ya registraste. Cada peso tiene
        que estar en una de las dos, nunca en las dos a la vez: si está en las dos, se resta dos veces y
        lo que se espera pagar sale menor de lo real. Esta pantalla revisa eso tarjeta por tarjeta y
        <b> abrirla no cambia ninguna cifra</b>.
      </div>

      <AvisoDeError error={error} />
      <AvisoDeError error={rechazo} />

      {acuse && (
        <div className="aviso ok" role="status" data-prueba="rec-acuse">
          <b className="tit">
            ✓ Listo, lo declarado quedó en{' '}
            {formatearImporte(acuse.renglon.importeDiferidoDeclarado, acuse.renglon.codigoISO)}
          </b>
          En <b>{acuse.renglon.nombreTarjeta}</b> sacaste de lo declarado{' '}
          {formatearImporte(acuse.tarjeta.importeRespaldoSinDescontar, acuse.renglon.codigoISO)}, que es
          lo que tus planes ya respaldaban. Ese dinero deja de restarse dos veces. El cambio quedó en el
          historial de la tarjeta, con tu nombre y la fecha de hoy.
        </div>
      )}

      {reporte && (
        <div className="card" style={{ margin: 12 }} data-prueba="rec-fecha">
          <span className="ayuda">Revisado con los datos de hoy, {formatearFecha(reporte.fechaLocal)}.</span>
        </div>
      )}

      {!cargando && reporte?.tarjetas.length === 0 && (
        <div className="card" style={{ margin: 12 }} data-prueba="rec-sin-tarjetas">
          <b>No tienes tarjetas de crédito activas</b>
          <span className="ayuda">Cuando registres una, aquí vas a ver su comparación.</span>
        </div>
      )}

      {reporte?.tarjetas.map((tarjeta) => (
        <article
          className="card"
          style={{ margin: 12 }}
          key={tarjeta.idCuenta}
          data-prueba={`rec-tarjeta-${tarjeta.idCuenta}`}
        >
          <div className="fila">
            <div className="crece">
              <b>
                {tarjeta.nombreTarjeta}
                {tarjeta.cuatroUltimosDigitos && <span className="ayuda"> ·{tarjeta.cuatroUltimosDigitos}</span>}
              </b>
              <small>de {tarjeta.nombrePropietario}</small>
            </div>
            {/* La ETIQUETA la arma el servidor (RN-733a): aquí no se decide
                ninguna palabra ni ningún color por cuenta propia. */}
            {tarjeta.etiqueta && (
              <span className="chip" data-prueba={`rec-etiqueta-${tarjeta.idCuenta}`}>{tarjeta.etiqueta}</span>
            )}
          </div>

          {/* Las dos partes, una junto a la otra: son cifras distintas y ninguna
              se deriva de las otras dos. */}
          <div className="fila">
            <span className="crece">Declaraste a mano <small>lo que aún no tiene compra registrada</small></span>
            <b data-prueba={`rec-declarado-${tarjeta.idCuenta}`}>
              {formatearImporte(tarjeta.importeDiferidoDeclarado, tarjeta.codigoISO)}
            </b>
          </div>
          <div className="fila">
            <span className="crece">Respaldado por tus planes</span>
            <b data-prueba={`rec-por-planes-${tarjeta.idCuenta}`}>
              {formatearImporte(tarjeta.importeDiferidoPorPlanes, tarjeta.codigoISO)}
            </b>
          </div>
          {tarjeta.importeRespaldoSinDescontar !== null
            && Number(tarjeta.importeRespaldoSinDescontar) > 0 && (
            <div className="fila" data-prueba={`rec-dobles-${tarjeta.idCuenta}`}>
              <span className="crece">
                De eso, está contado dos veces
                <small>se resta de lo que vas a pagar dos veces</small>
              </span>
              <b>{formatearImporte(tarjeta.importeRespaldoSinDescontar, tarjeta.codigoISO)}</b>
            </div>
          )}

          {/* La rama degradada: lo que no se pudo comparar viaja sin estado y con
              su aviso; el renglón NO se oculta y sus cifras ciertas se ven
              (criterio del 18, RNF-062). */}
          {tarjeta.advertencia && (
            <div className="aviso w" role="status" data-prueba={`rec-advertencia-${tarjeta.idCuenta}`}>
              ⚠ {tarjeta.advertencia}
            </div>
          )}

          {tarjeta.titulo && (
            <div
              className={tarjeta.claveEstado === ESTADOS_DE_RECONCILIACION.respaldoSinDescontar
                ? 'aviso w' : 'aviso i'}
              role="status"
              data-prueba={`rec-dice-${tarjeta.idCuenta}`}
            >
              <b className="tit">{tarjeta.titulo}</b>
              {tarjeta.cuerpo}
              {tarjeta.textoComplementoDePropietario && (
                <span className="ayuda" data-prueba={`rec-ajena-${tarjeta.idCuenta}`}>
                  {tarjeta.textoComplementoDePropietario}
                </span>
              )}
            </div>
          )}

          <div className="fila acciones">
            {/* EL BOTÓN SOLO EXISTE CON TRASLAPE Y SOLO PARA EL PROPIETARIO: lo
                decide el servidor con `puedeDescontar` (RN-727a, RN-735), no esta
                pantalla. Tras el disparo el estado cambia y el botón desaparece. */}
            {tarjeta.puedeDescontar && (
              <button
                className="btn"
                type="button"
                onClick={() => { setAcuse(null); setRechazo(null); setPorConfirmar(tarjeta); }}
                data-prueba={`rec-descontar-${tarjeta.idCuenta}`}
              >
                Descontar lo ya respaldado
              </button>
            )}
            <Link className="btn sec" to={`/cuentas/meses/en-curso?idCuenta=${tarjeta.idCuenta}`}>
              Registrar una compra
            </Link>
            <Link className="btn sec" to={`/cuentas/${tarjeta.idCuenta}/editar`}>
              Corregir en la tarjeta
            </Link>
          </div>
        </article>
      ))}

      <div className="card" style={{ margin: 12 }}>
        <Link className="btn ancho" to="/cuentas/meses/en-curso" data-prueba="rec-registrar">
          Registrar una compra a meses que empezó antes
        </Link>
        <Link className="btn sec ancho" to="/cuentas/vencimientos" data-prueba="rec-vencen">
          Ver en lo que vence
        </Link>
      </div>

      {/* LA PREGUNTA, CON LA CIFRA DE ANTES Y LA DE DESPUÉS (RN-727b). Se usa el
          componente ÚNICO de confirmación del núcleo (D-277): esta pantalla no se
          dibuja su propio diálogo. */}
      <ConfirmacionGeneral
        abierta={porConfirmar !== null}
        pregunta="¿Descontar lo ya respaldado?"
        consecuencia={porConfirmar
          ? `En ${porConfirmar.nombreTarjeta}, de lo que declaraste a mano se saca lo que tus planes `
            + `registrados ya respaldan: `
            + `${formatearImporte(porConfirmar.importeRespaldoSinDescontar, porConfirmar.codigoISO)}. `
            + `Lo que declaraste pasa de `
            + `${formatearImporte(porConfirmar.importeDiferidoDeclarado, porConfirmar.codigoISO)} a `
            + `${formatearImporte(
              String(Math.max(0, Number(porConfirmar.importeDiferidoDeclarado)
                - Number(porConfirmar.importeRespaldoSinDescontar)).toFixed(2)),
              porConfirmar.codigoISO)}.`
          : ''}
        detalle={porConfirmar
          ? [
            {
              clave: 'antes',
              texto: 'Lo declarado ahora',
              dato: formatearImporte(porConfirmar.importeDiferidoDeclarado, porConfirmar.codigoISO),
            },
            {
              clave: 'despues',
              texto: 'Lo declarado después',
              dato: formatearImporte(
                String(Math.max(0, Number(porConfirmar.importeDiferidoDeclarado)
                  - Number(porConfirmar.importeRespaldoSinDescontar)).toFixed(2)),
                porConfirmar.codigoISO),
            },
            {
              clave: 'planes',
              texto: 'Lo que respaldan tus planes (no cambia)',
              dato: formatearImporte(porConfirmar.importeDiferidoPorPlanes, porConfirmar.codigoISO),
            },
          ]
          : null}
        rotuloDeAceptar="Descontar"
        rotuloDeCancelar="Cancelar"
        tono={TONOS_DE_CONFIRMACION.NEUTRAL}
        enCurso={enCurso}
        alAceptar={confirmarDescuento}
        alCancelar={() => setPorConfirmar(null)}
        datoDePrueba="rec-confirmacion"
      />
    </Shell>
  );
}
